/**
 * Story 061 — Cascading Component Model Autocomplete.
 *
 * Single-select. Options sourced from Story 062's lean projection
 * (`GET /asset-component-models?lean=true`) which returns both the
 * tenant catalogue (`scope: 'tenant'`) AND the global catalogue
 * (`scope: 'global'`). The Autocomplete groups the dropdown into:
 *   - "Frequently used" — tenant rows
 *   - "All component models" — global rows
 * Mirrors Story 057's `AssetModelAutocomplete` grouping pattern.
 */
import { Autocomplete, TextField } from "@mui/material";
import type { ReactElement } from "react";
import { useReadOnlyContext } from "@aeriosiq/ui";
import type { AssetComponentModelSummary } from "../../component-models/queries/index.js";

export interface ComponentModelAutocompleteProps {
  value: string | null;
  options: readonly AssetComponentModelSummary[];
  onChange: (id: string | null) => void;
  disabled?: boolean;
  required?: boolean;
  loading?: boolean;
  ariaDisabled?: boolean;
  label?: string;
}

export function ComponentModelAutocomplete({
  value,
  options,
  onChange,
  disabled = false,
  required = false,
  loading = false,
  ariaDisabled = false,
  label = "Component Model",
}: ComponentModelAutocompleteProps): ReactElement {
  const selected = options.find((o) => o.id === value) ?? null;
  // SB2-CC-03 — forward read-only state from `<ReadOnlyModal>` context.
  const { readOnly } = useReadOnlyContext();
  return (
    <Autocomplete<AssetComponentModelSummary>
      options={[...options]}
      value={selected}
      disabled={disabled}
      readOnly={readOnly}
      loading={loading}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      getOptionLabel={(o) => o.name}
      groupBy={(o) =>
        o.scope === "tenant" ? "Frequently used" : "All component models"
      }
      onChange={(_e, next) => {
        onChange(next?.id ?? null);
      }}
      renderInput={(params) => {
        /* eslint-disable @typescript-eslint/no-explicit-any */
        const passThrough: any = params;
        return (
          <TextField
            {...passThrough}
            label={label}
            required={required}
            slotProps={{
              htmlInput: {
                ...params.inputProps,
                "aria-label": label,
                ...(required ? { "aria-required": true } : {}),
                ...(ariaDisabled
                  ? { "aria-disabled": true, readOnly: true }
                  : {}),
              },
            }}
            fullWidth
          />
        );
        /* eslint-enable */
      }}
    />
  );
}
